import React, { useEffect, useRef } from 'react';

import { App, Input, Typography } from 'antd';
import type { InputRef } from 'antd';

import { useFormik } from 'formik';
import { tap } from 'rxjs/operators';
import Modal from 'v2/components/Shared/Modal';
import { transferHistoryService } from 'v2/services/transferHistory';
import * as Yup from 'yup';

interface ICompositionModalProps {
  open: boolean;
  setClose: () => void;
  compositionId: number;
}

interface IFormModel {
  email: string;
}

function CompositionModal({ open, setClose, compositionId }: ICompositionModalProps) {
  const { message } = App.useApp();
  const inputRef = useRef<InputRef>(null);

  const mailForm = useFormik<IFormModel>({
    initialValues: {
      email: ''
    },
    validationSchema: Yup.object().shape({
      email: Yup.string().trim().email('O e-mail informado não é um e-mail válido!').required('E-mail obrigatório!')
    }),
    validateOnMount: false,
    validateOnBlur: true,
    validateOnChange: true,
    onSubmit: values => {
      transferHistoryService
        .setCompositionRequest(compositionId, values.email)
        .pipe(
          tap(
            () => {
              message.success(`Relatório será enviado para o email: ${values.email}`);
            },
            err => {
              message.error(typeof err === 'string' ? err : 'Não foi possível solicitar o relatório.');
            }
          )
        )
        .subscribe();
      handleCloseModal();
    }
  });

  const handleCloseModal = () => {
    mailForm.resetForm();
    setClose();
  };

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    mailForm.resetForm();
    const timeoutId = window.setTimeout(() => inputRef.current?.focus(), 0);

    return () => window.clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when modal opens
  }, [open]);

  const showEmailError = mailForm.submitCount > 0 || (mailForm.touched.email && mailForm.dirty);
  const emailError = showEmailError && mailForm.errors.email ? mailForm.errors.email : '';
  const hasValidEmail = !!mailForm.values.email.trim() && !mailForm.errors.email;

  return (
    <Modal
      keepMounted
      open={open}
      setClose={handleCloseModal}
      textTitle='Composição de saque'
      textReturnButton='Cancelar'
      textProceedButton='Enviar'
      handleProceedClick={() => mailForm.handleSubmit()}
      isProceedButtonDisabled={!hasValidEmail}
    >
      <div className='flex flex-col gap-3'>
        <Typography.Paragraph className='mb-0'>Informe o e-mail para envio do relatório.</Typography.Paragraph>

        <Input
          id='email'
          name='email'
          placeholder='E-mail'
          ref={inputRef}
          value={mailForm.values.email}
          onChange={mailForm.handleChange}
          onBlur={mailForm.handleBlur}
          status={emailError ? 'error' : undefined}
        />
        {emailError && <Typography.Text type='danger'>{emailError}</Typography.Text>}
      </div>
    </Modal>
  );
}

export default CompositionModal;
